/** Help §5.6 — The kick-off meeting (`kickoff/KickoffView.tsx`). */
import { helpSectionMeta } from '../help-sections.js';
import {
  Btn,
  Bullets,
  Callout,
  HelpSectionCard,
  Li,
  P,
  Screen,
  Shot,
  SubHead,
} from '../HelpPrimitives.js';

export function Kickoff(): React.JSX.Element {
  return (
    <HelpSectionCard meta={helpSectionMeta('kickoff')}>
      <Shot
        slug="kickoff"
        caption="The kick-off screen — questions grouped by category, each with its own answered/total counter."
      />

      <Callout title="Why this matters">
        <P>
          The better and more complete your answers here, the better the AI-written documents will
          be in the next stage — it’s worth being thorough.
        </P>
      </Callout>

      <SubHead>Working through the questions</SubHead>
      <Bullets>
        <Li>
          Questions are grouped by category, each with its own <Btn>answered/total</Btn> counter,
          and an overall progress counter reads <Btn>{'{answered}/{total} answered'}</Btn>.
        </Li>
        <Li>
          A <Btn>Missing data: {'{count}'} unanswered</Btn> notice lists what’s left — toggle{' '}
          <Btn>Show all</Btn> / <Btn>Show less</Btn> to expand or collapse it.
        </Li>
      </Bullets>

      <SubHead>Helpers</SubHead>
      <Bullets>
        <Li>
          <Btn>AI Pre-fill Questionnaire</Btn> re-reads the documents/links you added on the
          Pre-Kickoff screen — plus the transcript, if you have pasted one into the{' '}
          <Btn>Transcript</Btn> box below — and fills in whatever answers the AI can find there. The
          transcript wins over the documents when the two disagree. It never touches
          compensation/salary answers — those need a human or the dedicated salary lookup.
        </Li>
        <Li>
          <Btn>Search Company Website</Btn> reads the company&rsquo;s own website — a plain page
          fetch, no web search or search engine involved — and has the AI extract a profile from it:
          industry, size, and other facts, each one linked back to the page it came from. The result
          appears below in the <Screen>Company Facts</Screen> panel, where you can open those source
          pages yourself. It needs the company website set on the Pre-Kickoff screen first; until
          then the button stays disabled with a hint pointing you there.
        </Li>
        <Li>
          The toggle button switches between <Btn>Questionnaire</Btn> and <Btn>Meeting Notes</Btn>{' '}
          modes; Meeting Notes also has a free <Btn>Transcript</Btn> box.
        </Li>
      </Bullets>

      <SubHead>Finishing</SubHead>
      <Bullets>
        <Li>
          <Btn>Save</Btn> keeps your work.
        </Li>
        <Li>
          <Btn>Complete Meeting</Btn> closes the stage and triggers an <Btn>AI Processing</Btn> step
          in the background; once it finishes, the project moves on to the Generation stage
          automatically.
        </Li>
      </Bullets>
    </HelpSectionCard>
  );
}
